// src/pages/ExpensesPanel.js
import React, { useEffect, useRef, useState } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";

import "../css/ExpensesPanel.css";

const CATEGORIES = [
  "Rent",
  "Utilities",
  "Payroll",
  "Supplies",
  "Marketing",
  "Taxes",
  "Other",
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function ExpensesPanel({ month, year }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Rent");
  const [note, setNote] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [loading, setLoading] = useState(false);

  // Monthly KPIs (selected month/year)
  const [dailyRevenue, setDailyRevenue] = useState(0);
  const [eventRevenue, setEventRevenue] = useState(0);

  // Annual (independent) state
  const [annualYear, setAnnualYear] = useState(
    typeof year === "number" ? year : new Date().getFullYear()
  );
  const [yearRows, setYearRows] = useState([]); // [{monthIndex, daily, events, gross, expenses, net}]
  const [yearTotals, setYearTotals] = useState({
    daily: 0,
    events: 0,
    gross: 0,
    expenses: 0,
    net: 0,
  });
  const [yearLoading, setYearLoading] = useState(false);
  const [showYearSummary, setShowYearSummary] = useState(true);

  // Prevent multiple recurring copies for the same (month,year) view
  const copiedForRef = useRef(null);

  // Current date helpers (used for YTD and future-month dimming)
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIdx = now.getMonth(); // 0..11

  // ---------- LOADERS (Monthly) ----------
  const loadExpenses = async () => {
    const qExp = query(
      collection(db, "expenses"),
      where("month", "==", month),
      where("year", "==", year)
    );
    const snap = await getDocs(qExp);
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setItems(rows);
    return rows;
  };

  const loadRevenue = async () => {
    // Daily transactions (cash-basis)
    const txQ = query(
      collection(db, "transactions"),
      where("Month", "==", month),
      where("Year", "==", year)
    );
    const txSnap = await getDocs(txQ);
    const tx = txSnap.docs.map((d) => d.data());
    const daily = tx.reduce((s, t) => s + (t.TotalAmount || 0), 0);

    // Private events (deposit + totalPaidAmount)
    const evQ = query(
      collection(db, "privateEvents"),
      where("month", "==", month),
      where("year", "==", year)
    );
    const evSnap = await getDocs(evQ);
    const events = evSnap.docs.map((d) => d.data());
    const eventsTotal = events.reduce(
      (s, e) => s + (e.depositAmount || 0) + (e.totalPaidAmount || 0),
      0
    );

    setDailyRevenue(daily);
    setEventRevenue(eventsTotal);
  };

  const prevMonthYear = (m, y) => {
    if (m === 1) return { m: 12, y: y - 1 };
    return { m: m - 1, y };
  };

  // Auto-copy recurring from previous month (once per viewed month/year)
  const ensureRecurringForMonth = async (currentItems) => {
    const key = `${year}-${month}`;
    if (copiedForRef.current === key) return;

    const { m: pm, y: py } = prevMonthYear(month, year);

    const prevQ = query(
      collection(db, "expenses"),
      where("month", "==", pm),
      where("year", "==", py),
      where("recurring", "==", true)
    );
    const prevSnap = await getDocs(prevQ);
    const prevRows = prevSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    if (prevRows.length === 0) {
      copiedForRef.current = key;
      return;
    }

    // avoid duplicates by name+category and by sourceRecurringId
    const currentKeys = new Set(
      (currentItems || []).map(
        (it) =>
          `${(it.name || "").trim().toLowerCase()}|${(it.category || "")
            .trim()
            .toLowerCase()}`
      )
    );
    const currentSourceIds = new Set(
      (currentItems || []).map((it) => it.sourceRecurringId).filter(Boolean)
    );

    const toAdd = prevRows.filter((p) => {
      const nameCat = `${(p.name || "").trim().toLowerCase()}|${(
        p.category || ""
      )
        .trim()
        .toLowerCase()}`;
      const alreadyByKey = currentKeys.has(nameCat);
      const alreadyBySource = currentSourceIds.has(p.id);
      return !alreadyByKey && !alreadyBySource;
    });

    if (toAdd.length === 0) {
      copiedForRef.current = key;
      return;
    }

    await Promise.all(
      toAdd.map((p) =>
        addDoc(collection(db, "expenses"), {
          name: p.name,
          amount: p.amount,
          category: p.category,
          note: p.note || "",
          month,
          year,
          recurring: true,
          createdAt: Timestamp.now(),
          sourceRecurringId: p.id, // prevents re-copy
        })
      )
    );

    await loadExpenses();
    copiedForRef.current = key;
  };

  const loadAllMonthly = async () => {
    setLoading(true);
    const current = await loadExpenses();
    await ensureRecurringForMonth(current);
    await loadRevenue();
    setLoading(false);
  };

  // Run monthly loaders when month/year props change
  useEffect(() => {
    copiedForRef.current = null;
    loadAllMonthly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  // ---------- LOADERS (Annual, independent) ----------
  const loadYearSummary = async (yr) => {
    setYearLoading(true);

    // Prepare 12 buckets
    const dailyByMonth = Array(12).fill(0);
    const eventsByMonth = Array(12).fill(0);
    const expensesByMonth = Array(12).fill(0);

    // Transactions by year
    const txQ = query(collection(db, "transactions"), where("Year", "==", yr));
    const txSnap = await getDocs(txQ);
    txSnap.docs.forEach((docSnap) => {
      const d = docSnap.data();
      const mIndex = (Number(d.Month) || 1) - 1;
      if (mIndex >= 0 && mIndex < 12) {
        dailyByMonth[mIndex] += Number(d.TotalAmount || 0);
      }
    });

    // Private events by year
    const evQ = query(collection(db, "privateEvents"), where("year", "==", yr));
    const evSnap = await getDocs(evQ);
    evSnap.docs.forEach((docSnap) => {
      const d = docSnap.data();
      const mIndex = (Number(d.month) || 1) - 1;
      if (mIndex >= 0 && mIndex < 12) {
        const amt =
          Number(d.depositAmount || 0) + Number(d.totalPaidAmount || 0);
        eventsByMonth[mIndex] += amt;
      }
    });

    // Expenses by year
    const exQ = query(collection(db, "expenses"), where("year", "==", yr));
    const exSnap = await getDocs(exQ);
    exSnap.docs.forEach((docSnap) => {
      const d = docSnap.data();
      const mIndex = (Number(d.month) || 1) - 1;
      if (mIndex >= 0 && mIndex < 12) {
        expensesByMonth[mIndex] += Number(d.amount || 0);
      }
    });

    // Build rows for all months
    const rows = Array.from({ length: 12 }, (_, i) => {
      const daily = dailyByMonth[i];
      const events = eventsByMonth[i];
      const gross = daily + events;
      const expenses = expensesByMonth[i];
      const net = gross - expenses;
      return { monthIndex: i, daily, events, gross, expenses, net };
    });

    // Totals (YTD if current year)
    const sumUntil = yr === currentYear ? currentMonthIdx : 11;
    const totals = rows.slice(0, sumUntil + 1).reduce(
      (acc, r) => {
        acc.daily += r.daily;
        acc.events += r.events;
        acc.gross += r.gross;
        acc.expenses += r.expenses;
        acc.net += r.net;
        return acc;
      },
      { daily: 0, events: 0, gross: 0, expenses: 0, net: 0 }
    );

    setYearRows(rows);
    setYearTotals(totals);
    setYearLoading(false);
  };

  // Run annual loader when annualYear changes
  useEffect(() => {
    loadYearSummary(annualYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annualYear]);

  // ---------- MUTATIONS ----------
  const addExpense = async () => {
    const amt = Number(amount);
    if (!name || !amt) return;
    await addDoc(collection(db, "expenses"), {
      name,
      amount: amt,
      category,
      note,
      month,
      year,
      recurring,
      createdAt: Timestamp.now(),
    });
    setName("");
    setAmount("");
    setNote("");
    setRecurring(false);
    await loadAllMonthly(); // monthly only
  };

  const removeExpense = async (id) => {
    await deleteDoc(doc(db, "expenses", id));
    await loadAllMonthly(); // monthly only
  };

  // ---------- DERIVED (Monthly) ----------
  const expensesTotal = items.reduce((s, e) => s + (e.amount || 0), 0);
  const byCategory = items.reduce((acc, e) => {
    const c = e.category || "Uncategorized";
    acc[c] = (acc[c] || 0) + (e.amount || 0);
    return acc;
  }, {});
  const grossRevenue = dailyRevenue + eventRevenue;
  const net = grossRevenue - expensesTotal;

  // ---------- PRE-BUILD ANNUAL TABLE ROWS ----------
  const yearTableRows = yearRows.map((r) => {
    const isFuture =
      annualYear === currentYear && r.monthIndex > currentMonthIdx;
    return (
      <tr
        key={r.monthIndex}
        className={isFuture ? "future" : ""}
        title={isFuture ? "Future month (booked/planned)" : undefined}
      >
        <td>
          {MONTH_NAMES[r.monthIndex]}
          {isFuture ? " (future)" : ""}
        </td>
        <td className="td-right">${r.daily.toFixed(2)}</td>
        <td className="td-right">${r.events.toFixed(2)}</td>
        <td className="td-right">${r.gross.toFixed(2)}</td>
        <td className="td-right">${r.expenses.toFixed(2)}</td>
        <td className="td-right">${r.net.toFixed(2)}</td>
      </tr>
    );
  });

  // ---------- RENDER ----------
  return (
    <div className="expenses-panel">
      <div className="ep-header">
        <div>
          <h3 className="ep-title">
            Expenses &amp; Income — {year}-{String(month).padStart(2, "0")}
          </h3>
          <p className="ep-subtitle">
            Quick view of this month’s revenue, expenses, and net.
          </p>
        </div>
        <div className="ep-badge">
          <span className="dot" /> {MONTH_NAMES[month - 1]} {year}
        </div>
      </div>

      {/* KPIs (monthly) */}
      <div className="kpis">
        <div className="kpi">
          <div className="kpi-label">Daily Revenue</div>
          <div className="kpi-value">${Number(dailyRevenue).toFixed(2)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Events Revenue</div>
          <div className="kpi-value">${Number(eventRevenue).toFixed(2)}</div>
        </div>
        <div className="kpi kpi-accent">
          <div className="kpi-label">Gross Revenue</div>
          <div className="kpi-value">${Number(grossRevenue).toFixed(2)}</div>
        </div>
        <div className="kpi kpi-warn">
          <div className="kpi-label">Expenses</div>
          <div className="kpi-value">${Number(expensesTotal).toFixed(2)}</div>
        </div>
        <div className={`kpi ${net >= 0 ? "kpi-good" : "kpi-bad"}`}>
          <div className="kpi-label">Net</div>
          <div className="kpi-value">${Number(net).toFixed(2)}</div>
        </div>
      </div>

      {/* Add expense form */}
      <div className="card">
        <div className="card-title">Add Expense</div>
        <div className="expenses-form">
          <input
            placeholder="Name (e.g., Water bill)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Expense name"
          />
          <input
            placeholder="Amount (USD)"
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            aria-label="Expense amount"
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label="Expense category"
          >
            {CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <input
            className="flex-1"
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-label="Expense note"
          />
          <label className="checkbox" title="Automatically copy next month">
            <input
              type="checkbox"
              checked={recurring}
              onChange={(e) => setRecurring(e.target.checked)}
            />
            Recurring monthly
          </label>
          <button onClick={addExpense} className="btn-primary">
            + Add
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <>
          {/* MONTH TABLE */}
          <div className="card">
            <div className="card-title">This Month’s Expenses</div>
            <div className="table-wrap">
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Category</th>
                      <th className="td-right">Amount</th>
                      <th>Note</th>
                      <th>Recurring</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id}>
                        <td>{it.name}</td>
                        <td>
                          <span className="pill">{it.category || "—"}</span>
                        </td>
                        <td className="td-right">
                          ${(it.amount || 0).toFixed(2)}
                        </td>
                        <td className="td-note">{it.note || "—"}</td>
                        <td>
                          {it.recurring ? (
                            <span className="badge badge-green">Recurring</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <button
                            className="icon-btn"
                            onClick={() => removeExpense(it.id)}
                            title="Delete"
                            aria-label={`Delete ${it.name}`}
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={6} className="empty">
                          No expenses yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}>Total</td>
                      <td className="td-right">${expensesTotal.toFixed(2)}</td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* BY CATEGORY (monthly) */}
            <div className="bycat">
              <strong>By category:</strong>
              <div className="bycat-list">
                {Object.entries(byCategory).map(([k, v]) => (
                  <span key={k} className="bycat-item">
                    <span className="pill">{k}</span>
                    <span className="bycat-amt">${v.toFixed(2)}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* YEAR SUMMARY (Independent controls) */}
          <div className="card">
            <div className="card-title-row">
              <div className="card-title">Year Summary</div>
              <div className="year-controls">
                <button
                  className="icon-btn"
                  onClick={() => setAnnualYear((y) => y - 1)}
                  title="Previous year"
                >
                  ◀
                </button>
                <input
                  type="number"
                  min="2000"
                  max={new Date().getFullYear() + 1}
                  value={annualYear}
                  onChange={(e) => setAnnualYear(Number(e.target.value))}
                  className="year-input"
                  aria-label="Annual year"
                />
                <button
                  className="icon-btn"
                  onClick={() => setAnnualYear((y) => y + 1)}
                  title="Next year"
                >
                  ▶
                </button>
                <button
                  className="icon-btn"
                  onClick={() => setAnnualYear(currentYear)}
                  title="Jump to current year"
                >
                  This year
                </button>
                <button
                  className="icon-btn"
                  onClick={() => setShowYearSummary((s) => !s)}
                  title={showYearSummary ? "Hide summary" : "Show summary"}
                >
                  {showYearSummary ? "▾" : "▸"}
                </button>
              </div>
            </div>

            {showYearSummary && (
              <>
                {yearLoading ? (
                  <div className="loading">Loading year…</div>
                ) : (
                  <>
                    {/* Year KPIs */}
                    <div className="kpis kpis-compact">
                      <div className="kpi">
                        <div className="kpi-label">YTD Daily Revenue</div>
                        <div className="kpi-value">
                          ${yearTotals.daily.toFixed(2)}
                        </div>
                      </div>
                      <div className="kpi">
                        <div className="kpi-label">YTD Events Revenue</div>
                        <div className="kpi-value">
                          ${yearTotals.events.toFixed(2)}
                        </div>
                      </div>
                      <div className="kpi kpi-accent">
                        <div className="kpi-label">YTD Gross Revenue</div>
                        <div className="kpi-value">
                          ${yearTotals.gross.toFixed(2)}
                        </div>
                      </div>
                      <div className="kpi kpi-warn">
                        <div className="kpi-label">YTD Expenses</div>
                        <div className="kpi-value">
                          ${yearTotals.expenses.toFixed(2)}
                        </div>
                      </div>
                      <div
                        className={`kpi ${
                          yearTotals.net >= 0 ? "kpi-good" : "kpi-bad"
                        }`}
                      >
                        <div className="kpi-label">YTD Net</div>
                        <div className="kpi-value">
                          ${yearTotals.net.toFixed(2)}
                        </div>
                      </div>
                    </div>

                    {/* Year table */}
                    <div className="table-wrap">
                      <div className="table-scroll">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Month</th>
                              <th className="td-right">Daily</th>
                              <th className="td-right">Events</th>
                              <th className="td-right">Gross</th>
                              <th className="td-right">Expenses</th>
                              <th className="td-right">Net</th>
                            </tr>
                          </thead>
                          <tbody>{yearTableRows}</tbody>
                          <tfoot>
                            <tr>
                              <td>
                                Totals (YTD
                                {annualYear !== currentYear ? " full year" : ""}
                                )
                              </td>
                              <td className="td-right">
                                ${yearTotals.daily.toFixed(2)}
                              </td>
                              <td className="td-right">
                                ${yearTotals.events.toFixed(2)}
                              </td>
                              <td className="td-right">
                                ${yearTotals.gross.toFixed(2)}
                              </td>
                              <td className="td-right">
                                ${yearTotals.expenses.toFixed(2)}
                              </td>
                              <td className="td-right">
                                ${yearTotals.net.toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
